import { Box, Paper, Typography } from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import { useActivityFilters } from "../../hooks/useActivityFilters";
import { useCategoryCounts } from "../../hooks/useCategoryCounts";
import { WorkItemStateEnum } from "../../store/services/timelineApiRTK";
import {
  WorkItemFavoriteTypeEnum,
  useSetWorkItemFavoriteMutation,
} from "../../store/services/workItemService";
import { ActivityFilters as Filters } from "../../types/ActivityList.types";
import ActivityCardView from "./components/ActivityCardView";
import ActivityFilters from "./components/ActivityFilters";
import ActivityListView from "./components/ActivityListView";
import ActivityTabs from "./components/ActivityTabs";
import { useActiveUserWorkItems } from "../../hooks/useActiveUserWorkItems";

const ActivitiesView: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({
    search: "",
    category: "all",
    dateFilter: { from: null, to: null },
    showFavorites: false,
    showAll: false,
  });
  const { data: workItems = [] } = useActiveUserWorkItems({
    state: filters.showAll ? undefined : WorkItemStateEnum.Active,
    fromDate: filters.dateFilter.from
      ? format(filters.dateFilter.from, "yyyy-MM-dd")
      : undefined,
    toDate: filters.dateFilter.to
      ? format(filters.dateFilter.to, "yyyy-MM-dd")
      : undefined,
  });

  const [showFilters, setShowFilters] = useState(false);
  const isMobile = window.innerWidth <= 600;
  const [viewType, setViewType] = useState<"card" | "list">(
    isMobile ? "card" : "list"
  );

  const favorites = workItems
    .filter((item) => item.FavoriteType === WorkItemFavoriteTypeEnum.Favorite)
    .map((item) => item.Id);

  const [setFavorite] = useSetWorkItemFavoriteMutation();

  const filteredActivities = useActivityFilters(workItems, filters, favorites);
  const categoryCounts = useCategoryCounts(workItems, favorites);

  const toggleFavorite = (activityId: number): void => {
    setFavorite({
      workItemId: activityId,
      type: favorites.includes(activityId)
        ? WorkItemFavoriteTypeEnum.NotSet
        : WorkItemFavoriteTypeEnum.Favorite,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setFilters({ ...filters, category: newValue });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <ActivityFilters
            filters={filters}
            onFiltersChange={setFilters}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
          />

          <ActivityTabs
            tabValue={filters.category}
            onTabChange={handleTabChange}
            categoryCounts={categoryCounts}
            viewType={viewType}
            onViewTypeChange={setViewType}
          />
        </Box>
      </Paper>

      <Box sx={{ mb: 3 }}>
        {filteredActivities.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              Inga aktiviteter hittades
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Prova att justera din sökning eller filtrera på annat sätt
            </Typography>
          </Paper>
        ) : viewType === "card" ? (
          <ActivityCardView
            activities={filteredActivities}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
          />
        ) : (
          <ActivityListView
            activities={filteredActivities}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Visar {filteredActivities.length} av {workItems.length} aktiviteter
        </Typography>
      </Box>
    </Box>
  );
};

export default ActivitiesView;
